import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ThemeProvider,
  createTheme,
  Radio,
  RadioGroup,
  FormControlLabel,
  CircularProgress,
  Alert,
  Snackbar,
  Skeleton,
  InputAdornment,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { BASE_URL } from "../../../services/configUrls";

// Zoho Books-inspired theme
const theme = createTheme({
  typography: {
    fontFamily: '"Poppins", "Lato", "Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 13,
  },
  palette: {
    primary: {
      main: "#2D7FF9",
      light: "#5B9EFA",
      dark: "#1E5FD9",
    },
    success: {
      main: "#0BBE87",
    },
    error: {
      main: "#E85858",
    },
    background: {
      default: "#F9FAFB",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#212529",
      secondary: "#6C757D",
    },
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            backgroundColor: "#FFFFFF",
            "& fieldset": {
              borderColor: "#D1D5DB",
            },
            "&:hover fieldset": {
              borderColor: "#9CA3AF",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#2D7FF9",
              borderWidth: "1px",
            },
          },
          "& .MuiInputBase-input": {
            fontSize: "13px",
            padding: "10px 12px",
          },
          "& .MuiInputLabel-root": {
            fontSize: "13px",
            color: "#6C757D",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontSize: "13px",
          fontWeight: 500,
          padding: "8px 16px",
          borderRadius: "4px",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        },
        contained: {
          "&:hover": {
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: "13px",
          padding: "12px 16px",
          borderBottom: "1px solid #E5E7EB",
        },
        head: {
          fontWeight: 600,
          color: "#374151",
          backgroundColor: "#F9FAFB",
          borderBottom: "2px solid #E5E7EB",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          borderRadius: "6px",
        },
      },
    },
  },
});

const CreateFeatureComponent = () => {
  const [features, setFeatures] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [openAddFeatureDialog, setOpenAddFeatureDialog] = useState(false);
  const [newFeatureName, setNewFeatureName] = useState("");
  const [newFeatureType, setNewFeatureType] = useState("text");
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    featureId: null,
    featureName: "",
  });
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Get access token from localStorage
  const getAuthHeaders = () => {
    const accessToken = localStorage.getItem("accessToken");
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    };
  };

  // Fetch all features on component mount
  useEffect(() => {
    fetchAllFeatures();
  }, []);

  const fetchAllFeatures = async () => {
    try {
      setInitialLoading(true);
      const response = await fetch(`${BASE_URL}/accounts/all-feature`, {
        method: "GET",
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setFeatures(data);
      } else {
        showSnackbar("Failed to fetch features", "error");
      }
    } catch (error) {
      console.error("Error fetching features:", error);
      showSnackbar("Error fetching features", "error");
    } finally {
      setInitialLoading(false);
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Filter features based on search term
  const filteredFeatures = features.filter((feature) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      feature.feature_name.toLowerCase().includes(searchLower) ||
      feature.type.toLowerCase().includes(searchLower)
    );
  });

  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchTerm("");
  };

  // Handle add new feature
  const handleAddFeature = async () => {
    if (newFeatureName.trim()) {
      try {
        setLoading(true);
        const payload = {
          feature_name: newFeatureName.trim(),
          type:
            newFeatureType.charAt(0).toUpperCase() + newFeatureType.slice(1),
        };

        const response = await fetch(`${BASE_URL}/accounts/create-feature`, {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify(payload),
        });

        if (response.ok) {
          const data = await response.json();
          showSnackbar(
            data.message || `Feature created successfully`,
            "success"
          );
          setNewFeatureName("");
          setNewFeatureType("text");
          setOpenAddFeatureDialog(false);
          // Refresh the features list
          await fetchAllFeatures();
        } else {
          const errorData = await response.json();
          showSnackbar(errorData.detail || "Failed to create feature", "error");
        }
      } catch (error) {
        console.error("Error creating feature:", error);
        showSnackbar("Error creating feature", "error");
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle delete feature
  const handleDeleteFeatureClick = (featureId, featureName) => {
    setDeleteConfirmDialog({ open: true, featureId, featureName });
  };

  const handleDeleteFeatureConfirm = async () => {
    const { featureId } = deleteConfirmDialog;
    try {
      setDeleteLoading(true);
      const response = await fetch(
        `${BASE_URL}/accounts/delete-feature/${featureId}`,
        {
          method: "DELETE",
          headers: getAuthHeaders(),
        }
      );

      if (response.ok) {
        showSnackbar("Feature deleted successfully", "success");
        setDeleteConfirmDialog({
          open: false,
          featureId: null,
          featureName: "",
        });
        // Refresh the features list
        await fetchAllFeatures();
      } else {
        const errorData = await response.json();
        showSnackbar(errorData.message || "Failed to delete feature", "error");
      }
    } catch (error) {
      console.error("Error deleting feature:", error);
      showSnackbar("Error deleting feature", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteFeatureCancel = () => {
    setDeleteConfirmDialog({ open: false, featureId: null, featureName: "" });
  };

  // Skeleton loading component
  const SkeletonLoader = () => (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Skeleton variant="text" width={150} height={24} />
        <Skeleton
          variant="rectangular"
          width={150}
          height={36}
          sx={{ borderRadius: "4px" }}
        />
      </Box>

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ border: "1px solid #E5E7EB" }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "10%" }}>
                <Skeleton variant="text" width={20} />
              </TableCell>
              <TableCell sx={{ width: "60%" }}>
                <Skeleton variant="text" width={120} />
              </TableCell>
              <TableCell sx={{ width: "20%" }}>
                <Skeleton variant="text" width={60} />
              </TableCell>
              <TableCell sx={{ width: "10%", textAlign: "center" }}>
                <Skeleton variant="text" width={60} />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[1, 2, 3, 4, 5].map((item) => (
              <TableRow key={item}>
                <TableCell>
                  <Skeleton variant="text" width={20} />
                </TableCell>
                <TableCell>
                  <Skeleton variant="text" width="80%" />
                </TableCell>
                <TableCell>
                  <Skeleton
                    variant="rectangular"
                    width={70}
                    height={28}
                    sx={{ borderRadius: "4px" }}
                  />
                </TableCell>
                <TableCell sx={{ textAlign: "center" }}>
                  <Skeleton
                    variant="circular"
                    width={32}
                    height={32}
                    sx={{ mx: "auto" }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "#F9FAFB",
          py: 4,
          px: 3,
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: "auto" }}>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: "#212529",
                mb: 0.5,
              }}
            >
              Create Features
            </Typography>
            <Typography variant="body2" sx={{ color: "#6C757D" }}>
              Add and manage features with type definitions
            </Typography>
          </Box>

          {/* Main Content */}
          {initialLoading ? (
            <SkeletonLoader />
          ) : (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: "#374151",
                    fontSize: "13px",
                  }}
                >
                  Features List ({filteredFeatures.length}
                  {searchTerm && ` of ${features.length}`})
                </Typography>
                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                  {/* Search Bar */}
                  <TextField
                    size="small"
                    placeholder="Search features..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    sx={{
                      minWidth: 280,
                      "& .MuiOutlinedInput-root": {
                        paddingRight: searchTerm ? "8px" : "12px",
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: 20, color: "#6C757D" }} />
                        </InputAdornment>
                      ),
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={handleClearSearch}
                            edge="end"
                            sx={{
                              padding: "4px",
                              "&:hover": {
                                backgroundColor: "#F3F4F6",
                              },
                            }}
                          >
                            <ClearIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => setOpenAddFeatureDialog(true)}
                    size="small"
                    variant="contained"
                    sx={{
                      transition: "all 0.3s ease",
                      whiteSpace: "nowrap",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 8px rgba(45, 127, 249, 0.2)",
                      },
                    }}
                  >
                    Add New Feature
                  </Button>
                </Box>
              </Box>

              {features.length === 0 ? (
                <Box
                  sx={{
                    p: 6,
                    textAlign: "center",
                    border: "2px dashed #E5E7EB",
                    borderRadius: "6px",
                    backgroundColor: "#FAFBFC",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "#6C757D", mb: 2, fontSize: "13px" }}
                  >
                    No features created yet
                  </Typography>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => setOpenAddFeatureDialog(true)}
                    variant="contained"
                    size="small"
                    sx={{
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 12px rgba(45, 127, 249, 0.3)",
                      },
                    }}
                  >
                    Create Your First Feature
                  </Button>
                </Box>
              ) : filteredFeatures.length === 0 ? (
                <Box
                  sx={{
                    p: 6,
                    textAlign: "center",
                    border: "2px dashed #E5E7EB",
                    borderRadius: "6px",
                    backgroundColor: "#FAFBFC",
                  }}
                >
                  <SearchIcon sx={{ fontSize: 48, color: "#D1D5DB", mb: 2 }} />
                  <Typography
                    variant="body2"
                    sx={{ color: "#6C757D", fontSize: "13px" }}
                  >
                    No features found matching "{searchTerm}"
                  </Typography>
                </Box>
              ) : (
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ border: "1px solid #E5E7EB" }}
                >
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: "10%" }}>#</TableCell>
                        <TableCell sx={{ width: "60%" }}>
                          Feature Name
                        </TableCell>
                        <TableCell sx={{ width: "20%" }}>Type</TableCell>
                        <TableCell sx={{ width: "10%", textAlign: "center" }}>
                          Action
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredFeatures.map((feature, index) => (
                        <TableRow
                          key={feature.feature_id}
                          sx={{
                            "&:hover": {
                              backgroundColor: "#F9FAFB",
                            },
                            animation: "fadeInRow 0.3s ease-out",
                            animationDelay: `${index * 0.05}s`,
                            animationFillMode: "backwards",
                            "@keyframes fadeInRow": {
                              from: {
                                opacity: 0,
                                transform: "translateX(-10px)",
                              },
                              to: {
                                opacity: 1,
                                transform: "translateX(0)",
                              },
                            },
                          }}
                        >
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "13px",
                                color: "#6C757D",
                                fontWeight: 600,
                              }}
                            >
                              {index + 1}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "13px", color: "#212529" }}
                            >
                              {feature.feature_name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                px: 2,
                                py: 0.75,
                                borderRadius: "4px",
                                backgroundColor:
                                  feature.type.toLowerCase() === "text"
                                    ? "#DBEAFE"
                                    : "#E0E7FF",
                                color:
                                  feature.type.toLowerCase() === "text"
                                    ? "#1E40AF"
                                    : "#3730A3",
                                fontWeight: 600,
                                fontSize: "12px",
                                textTransform: "capitalize",
                              }}
                            >
                              {feature.type}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ textAlign: "center" }}>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleDeleteFeatureClick(
                                  feature.feature_id,
                                  feature.feature_name
                                )
                              }
                              color="error"
                              sx={{
                                transition: "all 0.2s ease",
                                "&:hover": {
                                  transform: "scale(1.1) rotate(5deg)",
                                },
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          )}
        </Box>
      </Box>

      {/* Add Feature Dialog */}
      <Dialog
        open={openAddFeatureDialog}
        onClose={() => {
          setOpenAddFeatureDialog(false);
          setNewFeatureName("");
          setNewFeatureType("text");
        }}
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          timeout: 400,
        }}
        PaperProps={{
          sx: {
            animation: openAddFeatureDialog ? "slideUp 0.3s ease-out" : "none",
            "@keyframes slideUp": {
              from: {
                opacity: 0,
                transform: "translateY(20px)",
              },
              to: {
                opacity: 1,
                transform: "translateY(0)",
              },
            },
          },
        }}
      >
        <DialogTitle sx={{ fontSize: "15px", fontWeight: 600 }}>
          Add New Feature
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Feature Name"
            fullWidth
            value={newFeatureName}
            onChange={(e) => setNewFeatureName(e.target.value)}
            placeholder="e.g., Virtual Internship, Technical Workshop"
            onKeyPress={(e) => {
              if (e.key === "Enter" && newFeatureName.trim()) {
                handleAddFeature();
              }
            }}
            sx={{ mb: 2 }}
          />

          <Box>
            <Typography
              variant="body2"
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                color: "#374151",
                mb: 1.5,
              }}
            >
              Feature Type
            </Typography>
            <RadioGroup
              row
              value={newFeatureType}
              onChange={(e) => setNewFeatureType(e.target.value)}
              sx={{ gap: 3 }}
            >
              <FormControlLabel
                value="text"
                control={
                  <Radio
                    size="small"
                    sx={{
                      "& .MuiSvgIcon-root": {
                        fontSize: 20,
                      },
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                      Text
                    </Typography>
                    <Typography
                      sx={{ fontSize: "11px", color: "#9CA3AF", mt: 0.25 }}
                    >
                      For status values like "Eligible", "TBA"
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="count"
                control={
                  <Radio
                    size="small"
                    sx={{
                      "& .MuiSvgIcon-root": {
                        fontSize: 20,
                      },
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                      Count
                    </Typography>
                    <Typography
                      sx={{ fontSize: "11px", color: "#9CA3AF", mt: 0.25 }}
                    >
                      For numeric values like "100", "50"
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setOpenAddFeatureDialog(false);
              setNewFeatureName("");
              setNewFeatureType("text");
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddFeature}
            variant="contained"
            disabled={!newFeatureName.trim() || loading}
            startIcon={loading && <CircularProgress size={16} />}
          >
            {loading ? "Adding..." : "Add Feature"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={handleDeleteFeatureCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            animation: deleteConfirmDialog.open
              ? "slideUp 0.3s ease-out"
              : "none",
            "@keyframes slideUp": {
              from: {
                opacity: 0,
                transform: "translateY(20px)",
              },
              to: {
                opacity: 1,
                transform: "translateY(0)",
              },
            },
          },
        }}
      >
        <DialogTitle sx={{ fontSize: "15px", fontWeight: 600 }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "13px", color: "#6C757D" }}>
            Are you sure you want to delete the feature "
            {deleteConfirmDialog.featureName}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteFeatureCancel} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteFeatureConfirm}
            variant="contained"
            color="error"
            disabled={deleteLoading}
            startIcon={
              deleteLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DeleteIcon />
              )
            }
          >
            {deleteLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
};

export default CreateFeatureComponent;
